import React from 'react';
import PropTypes from 'prop-types';
import TreeContainer from './TreeContainer';
import { treeSelectionNodeShape } from './helpers/TreeSelectionPropTypes';
import isNullOrUndefined from '@adsk/bim360-matrix-react-helpers/es/isNullOrUndefined';
import { getTreeForRender, getTreeForRenderAndSelectedNodes } from './helpers/nodeHelpers';
import './_TreeSelection.scss';

const isControlledSelectedNodesIds = props => 'selectedNodesIds' in props;

class TreeSelection extends React.Component {
  constructor(props) {
    super(props);

    const selectedNodesIds = isControlledSelectedNodesIds(props)
      ? props.selectedNodesIds
      : props.defaultSelectedNodeIds;

    const treeProps = props.multi
      ? getTreeForRenderAndSelectedNodes(props.treeData, selectedNodesIds, props.toggleAncestors)
      : getTreeForRender(props.treeData, props.selectedNodeId, props.toggleAncestors);

    this.state = {
      nodes: treeProps.nodes,
      selectedNodeId: props.selectedNodeId,
      selectedNodesIds,
      selectedNodes: props.multi ? treeProps.selectedNodes : [],
      width: props.fixedSize ? props.width : null,
      height: props.fixedSize ? props.maxHeight : null
    };

    this.handleChange = this.handleChange.bind(this);
    this.onSelect = this.onSelect.bind(this);
    this.onMultiSelect = this.onMultiSelect.bind(this);
    this.setContainer = this.setContainer.bind(this);
    this.getSelectMethod = this.getSelectMethod.bind(this);
  }

  componentDidMount() {
    if (this.props.fixedSize) {
      this.setState({
        width: this.props.width || this.container.clientWidth,
        height: this.props.maxHeight || this.container.clientHeight
      });
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      nextProps.treeData !== this.props.treeData ||
      nextProps.selectedNodeId !== this.props.selectedNodeId ||
      nextProps.selectedNodesIds !== this.props.selectedNodesIds ||
      nextProps.toggleAncestors !== this.props.toggleAncestors
    ) {
      const treeProps = this.props.multi
        ? getTreeForRenderAndSelectedNodes(
          nextProps.treeData,
          nextProps.selectedNodesIds,
          nextProps.toggleAncestors
        )
        : getTreeForRender(nextProps.treeData, nextProps.selectedNodeId, nextProps.toggleAncestors);

      this.setState({
        nodes: treeProps.nodes,
        selectedNodeId: nextProps.selectedNodeId,
        selectedNodesIds: isControlledSelectedNodesIds(nextProps)
          ? nextProps.selectedNodesIds
          : this.state.selectedNodesIds,
        selectedNodes: this.props.multi ? treeProps.selectedNodes : [],
        width: nextProps.fixedSize ? this.props.width || this.container.clientWidth : null,
        height: nextProps.fixedSize ? this.props.maxHeight || this.container.clientHeight : null
      });
    } else if (
      (nextProps.fixedSize && this.props.width !== nextProps.width) ||
      this.props.maxHeight !== nextProps.maxHeight
    ) {
      this.setState({
        width: nextProps.width,
        height: nextProps.maxHeight
      });
    }
  }

  setContainer(ref) {
    this.container = ref;
  }

  handleChange(nodes) {
    this.setState({ nodes });
  }

  onSelect(data, isSelected) {
    if (isSelected) {
      return;
    }
    this.setState({ selectedNodeId: data.id });
    this.props.onChange(data);
  }

  onMultiSelect(data, isSelected) {
    let selectedNodes;
    if (isSelected) {
      selectedNodes = this.state.selectedNodes.filter(nodeId => nodeId.id !== data.id);
    } else {
      selectedNodes = [...this.state.selectedNodes, data];
    }
    this.setState({
      selectedNodes
    });
    if (!isControlledSelectedNodesIds(this.props)) {
      this.setState({ selectedNodesIds: selectedNodes.map(node => node.id) });
    }
    if (this.props.onChange) {
      this.props.onChange(selectedNodes);
    }
  }

  getSelectMethod() {
    return this.props.multi ? this.onMultiSelect : this.onSelect;
  }

  render() {
    const { nodes, selectedNodeId, selectedNodesIds, width, height: maxHeight } = this.state;
    const {
      itemRenderer,
      fetchNode,
      onLoadMore,
      rightArrow,
      onlyLeafSelection,
      loadMoreText,
      errorLoadingNodeText,
      onToggleClick,
      multi,
      disabledNodesIds,
      nodeMarginLeft,
      dontOverrideHasMoreFolderContents
    } = this.props;
    return (
      <div className="TreeSelection" ref={this.setContainer} style={{ width }}>
        {this.props.fixedSize && isNullOrUndefined(width) ? null : (
          <TreeContainer
            nodes={nodes}
            onChange={this.handleChange}
            onSelect={this.getSelectMethod()}
            onLoadMore={onLoadMore}
            width={width}
            height={maxHeight}
            fetchNode={fetchNode}
            rightArrow={rightArrow}
            onlyLeafSelection={onlyLeafSelection}
            multi={multi}
            selectedNodesIds={selectedNodesIds}
            selectedNodeId={selectedNodeId}
            disabledNodesIds={disabledNodesIds}
            itemRenderer={itemRenderer}
            onToggleClick={onToggleClick}
            loadMoreText={loadMoreText}
            errorLoadingNodeText={errorLoadingNodeText}
            nodeMarginLeft={nodeMarginLeft}
            dontOverrideHasMoreFolderContents={dontOverrideHasMoreFolderContents}
          />
        )}
      </div>
    );
  }
}

TreeSelection.propTypes = {
  treeData: PropTypes.oneOfType([PropTypes.arrayOf(treeSelectionNodeShape), treeSelectionNodeShape])
    .isRequired,
  selectedNodeId: PropTypes.string,
  multi: PropTypes.bool,
  defaultSelectedNodeIds: PropTypes.arrayOf(PropTypes.string),
  selectedNodesIds: PropTypes.arrayOf(PropTypes.string),
  disabledNodesIds: PropTypes.arrayOf(PropTypes.string),
  itemRenderer: PropTypes.func,
  onChange: PropTypes.func,
  toggleAncestors: PropTypes.bool,
  fixedSize: PropTypes.bool,
  maxHeight: PropTypes.number,
  width: PropTypes.number,
  fetchNode: PropTypes.func,
  onLoadMore: PropTypes.func,
  rightArrow: PropTypes.bool,
  onlyLeafSelection: PropTypes.bool,
  loadMoreText: PropTypes.string,
  errorLoadingNodeText: PropTypes.string,
  onToggleClick: PropTypes.func,
  nodeMarginLeft: PropTypes.number,
  dontOverrideHasMoreFolderContents: PropTypes.bool
};

TreeSelection.defaultProps = {
  toggleAncestors: true,
  itemRenderer: props => props.children,
  maxHeight: 0,
  disabledNodesIds: []
};

export default TreeSelection;
